// ============================================================================
// The estate view — every active supplier, what's driving their state, and why
// ============================================================================
// The Compliance Overview tab needs three things the database cannot tell it:
//
//   1. WHO is red. `suppliers.compliance_status` is a single letter with no
//      attribution, so the tab could show "11 red" and not name them.
//   2. WHY they're red. compute_supplier_compliance_status returns red for a
//      rejected document, for a document that has never been supplied, and for
//      an approved certificate that lapsed — all through one bool_or. Those
//      three want completely different responses from a human.
//   3. WHICH document is driving it, so a row can say something more useful
//      than "red".
//
// (2) is the one that matters. Investigated 2026-08-14: the red wall dates from
// 2026-05-supplier-compliance-incomplete-is-red.sql, which correctly made
// zero-document suppliers red and then backfilled the whole estate in one
// statement. That merged "we have never asked this supplier for anything" into
// the same signal as "this supplier's cover has lapsed". Splitting them back
// out is this module's main job.
//
// ⚠️ THIS MODULE DOES NOT DECIDE A SUPPLIER'S STATE. `compliance_status` on the
// supplier row remains the only authority — it is what gates awarding, and
// recomputing it here would be a second implementation that drifts from the
// SQL (see [[one-helper-dedup]], which is exactly this bug). What's derived
// here is the EXPLANATION of a state the database has already decided. When the
// two disagree, `stateMatchesEvidence` goes false and the caller should say so
// rather than quietly showing the derived answer.
//
// The effective-requirement set mirrors compute_supplier_compliance_status:
//   requirements for the supplier's risk tier
//   UNION  per-supplier rows flagged risk_override_applicable
//   MINUS  per-supplier rows flagged not_required
//   INTERSECT documents that are actually live (see lib/liveDocuments — a
//            requirement naming an archived or never-published document is a
//            phantom and must not be chased)
//
// Pure. No Supabase, no dates beyond the injected `todayIso`.
// ============================================================================

import { DOCUMENT_STATES, todayIso as todayIsoOf, daysBetweenIso } from './compliance';
import { supplierDocumentEntries } from './complianceRecord';
import { isLiveDocument } from './liveDocuments';

// Why a document is a problem. Deliberately finer-grained than DOCUMENT_STATES,
// because DOCUMENT_STATES.OUTSTANDING covers two situations with opposite
// remedies:
//
//   NEVER_SUPPLIED  the supplier has done nothing with it, ever. Usually an
//                   onboarding gap — chase the supplier's setup, not the doc.
//   REJECTED        they supplied it and Fusion turned it down. Chase a
//                   replacement.
//
// Both read 'outstanding' from the state machine, and both read 'red' from the
// database. Only this distinction tells you which conversation to have.
export const DRIVERS = {
  EXPIRED:             'expired',
  NEVER_SUPPLIED:      'never_supplied',
  REJECTED:            'rejected',
  NEEDS_CLARIFICATION: 'needs_clarification',
  AWAITING_REVIEW:     'awaiting_review',
  EXPIRING_SOON:       'expiring_soon',
  APPROVED:            'approved',
  NOT_REQUIRED:        'not_required',
};

// Worst first. Drives which document gets named as the row's driver, and the
// default sort of the supplier list. Expired outranks never-supplied because a
// lapsed certificate is a live exposure, where a never-supplied one is usually
// a supplier who was never switched on properly.
const DRIVER_RANK = [
  DRIVERS.EXPIRED,
  DRIVERS.NEVER_SUPPLIED,
  DRIVERS.REJECTED,
  DRIVERS.NEEDS_CLARIFICATION,
  DRIVERS.AWAITING_REVIEW,
  DRIVERS.EXPIRING_SOON,
  DRIVERS.APPROVED,
  DRIVERS.NOT_REQUIRED,
];
const rankOf = (driver) => {
  const i = DRIVER_RANK.indexOf(driver);
  return i === -1 ? DRIVER_RANK.length : i;
};

// Which drivers make a supplier un-awardable. Mirrors the red branch of
// compute_supplier_compliance_status — kept as a named set so the tab's
// "cannot award these suppliers" copy and the counts agree.
export const RED_DRIVERS = new Set([
  DRIVERS.EXPIRED,
  DRIVERS.NEVER_SUPPLIED,
  DRIVERS.REJECTED,
]);

// A document's state plus the evidence, collapsed to one reason.
export function driverFor(entry) {
  if (!entry) return null;
  switch (entry.state) {
    case DOCUMENT_STATES.NOT_REQUIRED:        return DRIVERS.NOT_REQUIRED;
    case DOCUMENT_STATES.EXPIRED:             return DRIVERS.EXPIRED;
    case DOCUMENT_STATES.NEEDS_CLARIFICATION: return DRIVERS.NEEDS_CLARIFICATION;
    case DOCUMENT_STATES.AWAITING_REVIEW:     return DRIVERS.AWAITING_REVIEW;
    case DOCUMENT_STATES.EXPIRING_SOON:       return DRIVERS.EXPIRING_SOON;
    case DOCUMENT_STATES.APPROVED:            return DRIVERS.APPROVED;
    case DOCUMENT_STATES.OUTSTANDING:
      // The split the whole tab exists to make. `actioned` is true if the
      // supplier has ever uploaded, signed or acknowledged this document — so
      // an outstanding document they HAVE touched was rejected, and one they
      // haven't was never supplied at all.
      return entry.actioned ? DRIVERS.REJECTED : DRIVERS.NEVER_SUPPLIED;
    default:
      return DRIVERS.NEVER_SUPPLIED;
  }
}

// Human wording for a driver. Written for someone deciding what to do next, so
// each one implies its own remedy.
export const DRIVER_LABEL = {
  [DRIVERS.EXPIRED]:             'Expired',
  [DRIVERS.NEVER_SUPPLIED]:      'Never supplied',
  [DRIVERS.REJECTED]:            'Rejected — needs replacing',
  [DRIVERS.NEEDS_CLARIFICATION]: 'Awaiting supplier reply',
  [DRIVERS.AWAITING_REVIEW]:     'Awaiting your review',
  [DRIVERS.EXPIRING_SOON]:       'Expiring soon',
  [DRIVERS.APPROVED]:            'Current',
  [DRIVERS.NOT_REQUIRED]:        'Not required',
};

// The effective required document keys for one supplier, mirroring the SQL.
export function effectiveRequiredKeys({ supplier, requirements = [], supplierRecords = [], liveKeys }) {
  const keys = new Set();
  for (const r of requirements) {
    if (supplier?.risk_flag && r?.risk_level === supplier.risk_flag && r.document_key) keys.add(r.document_key);
  }
  for (const rec of supplierRecords) {
    if (rec?.risk_override_applicable && rec.document_key) keys.add(rec.document_key);
  }
  for (const rec of supplierRecords) {
    if (rec?.not_required && rec.document_key) keys.delete(rec.document_key);
  }
  // A requirement pointing at an archived or unpublished document is a phantom.
  const live = liveKeys instanceof Set ? liveKeys : new Set(liveKeys || []);
  const out = new Set();
  for (const k of keys) if (live.has(k)) out.add(k);
  return out;
}

// English pluralisation for the counts this tab is full of. Irregulars aren't
// worth a library here, but "1 documents" on a dashboard is the kind of thing
// that quietly costs a product its credibility.
export function plural(n, singular, pluralForm) {
  const word = n === 1 ? singular : (pluralForm || `${singular}s`);
  return `${n} ${word}`;
}

// One row per active supplier.
//
//   suppliers        active suppliers only — the caller filters, because
//                    "active" is a supplier-lifecycle question, not a
//                    compliance one
//   requirements     risk_document_requirements rows { risk_level, document_key }
//   liveDocs         fusion_documents rows { compliance_doc_key, document_name,
//                    satisfied_by, is_active, status }
//   records          supplier_onboarding_documents rows, INCLUDING not_required
//                    ones (they're needed to subtract a waiver, so the caller
//                    must not pre-filter them out)
//   acknowledgements document_acknowledgements rows
export function buildEstate({
  suppliers = [],
  requirements = [],
  liveDocs = [],
  records = [],
  acknowledgements = [],
  options = {},
} = {}) {
  const today = options.todayIso || todayIsoOf();
  const stateOptions = { todayIso: today, ...(options.stateOptions || {}) };

  const liveByKey = new Map();
  for (const d of liveDocs) {
    if (!d?.compliance_doc_key || !isLiveDocument(d)) continue;
    // Newest live version wins if a key has several rows — same reason the SQL
    // orders by version_value.
    const seen = liveByKey.get(d.compliance_doc_key);
    if (!seen || (d.version_value || 0) > (seen.version_value || 0)) liveByKey.set(d.compliance_doc_key, d);
  }
  const liveKeys = new Set(liveByKey.keys());

  const recordsBySupplier = new Map();
  for (const r of records) {
    if (!r?.supplier_id) continue;
    if (!recordsBySupplier.has(r.supplier_id)) recordsBySupplier.set(r.supplier_id, []);
    recordsBySupplier.get(r.supplier_id).push(r);
  }
  const acksBySupplier = new Map();
  for (const a of acknowledgements) {
    if (!a?.supplier_id) continue;
    if (!acksBySupplier.has(a.supplier_id)) acksBySupplier.set(a.supplier_id, []);
    acksBySupplier.get(a.supplier_id).push(a);
  }

  const rows = suppliers.map((supplier) => {
    const supplierRecords = recordsBySupplier.get(supplier.id) || [];
    const supplierAcks = acksBySupplier.get(supplier.id) || [];
    const required = effectiveRequiredKeys({ supplier, requirements, supplierRecords, liveKeys });

    const requiredDocs = Array.from(required).map((key) => {
      const doc = liveByKey.get(key);
      return {
        key,
        label: doc?.document_name || key,
        satisfiedBy: doc?.satisfied_by || null,
      };
    });

    const entries = supplierDocumentEntries({
      requiredDocs,
      records: supplierRecords,
      acknowledgements: supplierAcks,
      options: stateOptions,
    }).map((e) => {
      const driver = driverFor(e);
      const expiry = e.record?.expiry_date || null;
      return {
        ...e,
        driver,
        label: e.meta?.label || e.key,
        expiryDate: expiry,
        daysToExpiry: expiry ? daysBetweenIso(today, expiry) : null,
      };
    });

    const byDriver = (d) => entries.filter(e => e.driver === d);
    const expiredDocs      = byDriver(DRIVERS.EXPIRED);
    const neverSuppliedDocs = byDriver(DRIVERS.NEVER_SUPPLIED);
    const rejectedDocs     = byDriver(DRIVERS.REJECTED);
    const expiringDocs     = byDriver(DRIVERS.EXPIRING_SOON);

    // Worst document, by DRIVER_RANK then by soonest expiry within a driver.
    const driver = [...entries].sort((a, b) => {
      const r = rankOf(a.driver) - rankOf(b.driver);
      if (r !== 0) return r;
      if (a.expiryDate && b.expiryDate) return a.expiryDate < b.expiryDate ? -1 : 1;
      if (a.expiryDate) return -1;
      if (b.expiryDate) return 1;
      return String(a.label).localeCompare(String(b.label));
    })[0] || null;

    const state = supplier.compliance_status === 'green' ? 'green'
                : supplier.compliance_status === 'amber' ? 'amber'
                : supplier.compliance_status === 'red'   ? 'red'
                : 'not_set';

    // The evidence's own verdict, used ONLY to detect disagreement with the
    // database — never to overrule it.
    const evidenceState = (expiredDocs.length || neverSuppliedDocs.length || rejectedDocs.length) ? 'red'
      : (expiringDocs.length || byDriver(DRIVERS.AWAITING_REVIEW).length || byDriver(DRIVERS.NEEDS_CLARIFICATION).length) ? 'amber'
      : 'green';

    return {
      supplier,
      supplierId: supplier.id,
      name: supplier.supplier_name || '',
      state,
      entries,
      driver,
      driverLabel: driver ? DRIVER_LABEL[driver.driver] : null,
      counts: {
        expired: expiredDocs.length,
        neverSupplied: neverSuppliedDocs.length,
        rejected: rejectedDocs.length,
        expiringSoon: expiringDocs.length,
        required: entries.length,
      },
      // Expiry is the useful date only while something is wrong — it's the date
      // you'd chase against. A green supplier's expiry is months out and says
      // nothing; what you want there is when the state was last written, which
      // is the closest thing to "last assessed" the schema has.
      relevantDate: (state === 'green' ? null : driver?.expiryDate) || supplier.compliance_status_set_at || null,
      relevantDateKind: (state !== 'green' && driver?.expiryDate) ? 'expiry'
        : (supplier.compliance_status_set_at ? 'assessed' : null),
      evidenceState,
      stateMatchesEvidence: state === 'not_set' ? true : state === evidenceState,
    };
  });

  // Document-level totals, so the red breakdown sums cleanly. Supplier-level
  // counts would overlap — one supplier can hold both an expired document and
  // a never-supplied one — and two tiles that double-count are worse than one
  // that merges.
  const totals = {
    suppliers: rows.length,
    expiredDocs:       rows.reduce((n, r) => n + r.counts.expired, 0),
    neverSuppliedDocs: rows.reduce((n, r) => n + r.counts.neverSupplied, 0),
    rejectedDocs:      rows.reduce((n, r) => n + r.counts.rejected, 0),
    expiringDocs:      rows.reduce((n, r) => n + r.counts.expiringSoon, 0),
    suppliersWithExpired:       rows.filter(r => r.counts.expired > 0).length,
    suppliersWithNeverSupplied: rows.filter(r => r.counts.neverSupplied > 0).length,
    suppliersWithRejected:      rows.filter(r => r.counts.rejected > 0).length,
    disagreements: rows.filter(r => !r.stateMatchesEvidence).length,
  };

  return { rows, totals, liveByKey };
}

// Forward view. Windows are cumulative-exclusive: a document 30 days out
// appears in the 30 bucket, not the 7 and not the 60.
//
// Boundary rule, stated because the verification list checks it: a document
// expiring in EXACTLY 30 days lands in the 30 bucket (`days <= edge`).
export const EXPIRY_WINDOWS = [7, 30, 60, 90];

export function expiringWindows(rows = [], { windows = EXPIRY_WINDOWS } = {}) {
  const buckets = windows.map(days => ({ days, docs: [] }));
  for (const row of rows) {
    for (const e of row.entries) {
      // Already lapsed is not a forward view — that's the red breakdown's job.
      if (e.daysToExpiry == null || e.daysToExpiry < 0) continue;
      // Only documents that are actually current can "expire soon". An
      // unapproved upload has no meaningful expiry yet.
      if (e.driver !== DRIVERS.EXPIRING_SOON && e.driver !== DRIVERS.APPROVED) continue;
      const bucket = buckets.find(b => e.daysToExpiry <= b.days);
      if (bucket) bucket.docs.push({ ...e, row });
    }
  }
  for (const b of buckets) {
    b.docs.sort((a, z) => (a.daysToExpiry - z.daysToExpiry) || String(a.row.name).localeCompare(String(z.row.name)));
  }
  return buckets;
}

// How many suppliers hold each document type.
//
// The denominator is NOT the supplier count. Requirements are per risk tier, so
// a document only asked of high-risk suppliers should read 4 / 4 rather than
// 4 / 16 — otherwise the list invents a compliance problem that doesn't exist.
export function coverageByDocument(rows = []) {
  const map = new Map();
  for (const row of rows) {
    for (const e of row.entries) {
      if (!map.has(e.key)) map.set(e.key, { key: e.key, label: e.label, required: 0, held: 0, missingSupplierIds: [] });
      const c = map.get(e.key);
      c.required += 1;
      // "Held" means current — approved and not lapsed. Awaiting review is not
      // held yet, and saying otherwise would overstate coverage.
      if (e.driver === DRIVERS.APPROVED || e.driver === DRIVERS.EXPIRING_SOON) c.held += 1;
      else c.missingSupplierIds.push(row.supplierId);
    }
  }
  return Array.from(map.values())
    .sort((a, b) => (a.held / (a.required || 1)) - (b.held / (b.required || 1)) || a.label.localeCompare(b.label));
}

// Default order for the supplier list: worst state first, then soonest expiry,
// then name. "Worst" uses the DATABASE state, not the derived one.
const STATE_ORDER = { red: 0, amber: 1, not_set: 2, green: 3 };
export function sortEstateRows(rows = []) {
  return [...rows].sort((a, b) => {
    const s = (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9);
    if (s !== 0) return s;
    const ad = a.driver?.expiryDate, bd = b.driver?.expiryDate;
    if (ad && bd && ad !== bd) return ad < bd ? -1 : 1;
    if (ad && !bd) return -1;
    if (bd && !ad) return 1;
    return a.name.localeCompare(b.name);
  });
}
